/**
 * Level-Aware Thinking — unintended level shift (v0.1).
 *
 * When someone answers from a different logical level than the question
 * requires — without noticing — coherence breaks down for both speaker and listener.
 */

export type LogicalLevel =
  | 'environment'
  | 'behavior'
  | 'capabilities'
  | 'values'
  | 'identity'
  | 'purpose';

export type LevelShiftMarker =
  | 'altitude-hijack'
  | 'question-substitution'
  | 'coherence-collapse';

export type LevelAwareStep =
  | 'intro'
  | 'pattern'
  | 'capture'
  | 'diagnose'
  | 'reanchor'
  | 'results';

export const LEVEL_AWARE_STEPS: { id: LevelAwareStep; label: string; short: string }[] = [
  { id: 'intro', label: 'The phenomenon', short: 'Start' },
  { id: 'pattern', label: 'Three markers', short: 'Pattern' },
  { id: 'capture', label: 'Capture a moment', short: 'Capture' },
  { id: 'diagnose', label: 'Diagnose the shift', short: 'Diagnose' },
  { id: 'reanchor', label: 'Re-anchor', short: 'Re-anchor' },
  { id: 'results', label: 'Your protocol', short: 'Done' },
];

export const LOGICAL_LEVELS: {
  id: LogicalLevel;
  label: string;
  questionWord: string;
  description: string;
}[] = [
  {
    id: 'environment',
    label: 'Environment',
    questionWord: 'Where / when / with whom?',
    description: 'The situation, context, and external conditions.',
  },
  {
    id: 'behavior',
    label: 'Behavior',
    questionWord: 'What?',
    description: 'What you say and do — observable actions and stakeholders.',
  },
  {
    id: 'capabilities',
    label: 'Capabilities',
    questionWord: 'How?',
    description: 'Skills and knowledge that enable your behavior.',
  },
  {
    id: 'values',
    label: 'Values & Beliefs',
    questionWord: 'Why?',
    description: 'Principles and truths that drive you in this situation.',
  },
  {
    id: 'identity',
    label: 'Identity',
    questionWord: 'Who?',
    description: 'Sense of self or professional role — who you are.',
  },
  {
    id: 'purpose',
    label: 'Purpose / Mission',
    questionWord: 'What for?',
    description: 'Connection to something bigger — impact beyond yourself.',
  },
];

export const LEVEL_SHIFT_MARKERS: {
  id: LevelShiftMarker;
  label: string;
  description: string;
  signal: string;
}[] = [
  {
    id: 'altitude-hijack',
    label: 'Altitude hijack',
    description: 'You answered from a higher or lower level than the question required.',
    signal: 'Identity or purpose content in response to a behavior or stakeholder question.',
  },
  {
    id: 'question-substitution',
    label: 'Question substitution',
    description: 'You replaced the question with a different one internally.',
    signal: 'They asked "serve who?" and you answered "who I am as a servant."',
  },
  {
    id: 'coherence-collapse',
    label: 'Coherence collapse',
    description: 'Thinking became nonlinear because levels were collapsed into one answer.',
    signal: 'Fog, rambling, or "I\'m not sure why we\'re talking about this now."',
  },
];

export const REANCHOR_SCRIPTS = [
  {
    situation: 'Answered from identity; question was behavior',
    script:
      'You\'re answering from identity, but my question was at the behavior level. Let\'s stay with the specific action or stakeholder before we explore who you are.',
  },
  {
    situation: 'Jumped from behavior to purpose',
    script:
      'You jumped from behavior to purpose. Let\'s stay with what you would do tomorrow — then we can connect it to why it matters.',
  },
  {
    situation: 'Values flooding a capability question',
    script:
      'That\'s a values answer. The question was about how — what skills or methods would someone observe?',
  },
];

export interface LevelShiftScenario {
  id: string;
  title: string;
  questionAsked: string;
  questionLevel: LogicalLevel | '';
  responseGiven: string;
  responseLevel: LogicalLevel | '';
  markers: LevelShiftMarker[];
  diagnosisNotes: string;
  reanchoredResponse: string;
  coachMove: string;
}

export interface LevelAwareState {
  scenario: LevelShiftScenario;
  completedAt?: string;
}

export function createScenario(): LevelShiftScenario {
  return {
    id: crypto.randomUUID(),
    title: '',
    questionAsked: '',
    questionLevel: '',
    responseGiven: '',
    responseLevel: '',
    markers: [],
    diagnosisNotes: '',
    reanchoredResponse: '',
    coachMove: '',
  };
}

export function createDefaultLevelAwareState(): LevelAwareState {
  return { scenario: createScenario() };
}

export function stepIndex(step: LevelAwareStep): number {
  return LEVEL_AWARE_STEPS.findIndex(s => s.id === step);
}

export function nextStep(step: LevelAwareStep): LevelAwareStep | null {
  const idx = stepIndex(step);
  if (idx < 0 || idx >= LEVEL_AWARE_STEPS.length - 1) return null;
  return LEVEL_AWARE_STEPS[idx + 1].id;
}

export function prevStep(step: LevelAwareStep): LevelAwareStep | null {
  const idx = stepIndex(step);
  if (idx <= 0) return null;
  return LEVEL_AWARE_STEPS[idx - 1].id;
}

const LEVEL_ORDER: LogicalLevel[] = [
  'environment',
  'behavior',
  'capabilities',
  'values',
  'identity',
  'purpose',
];

export function levelIndex(level: LogicalLevel): number {
  return LEVEL_ORDER.indexOf(level);
}

export function suggestMarkers(scenario: LevelShiftScenario): LevelShiftMarker[] {
  const suggested = new Set<LevelShiftMarker>();
  const { questionLevel, responseLevel, responseGiven, questionAsked } = scenario;

  if (questionLevel && responseLevel) {
    const qIdx = levelIndex(questionLevel);
    const rIdx = levelIndex(responseLevel);
    if (qIdx >= 0 && rIdx >= 0 && Math.abs(qIdx - rIdx) >= 2) {
      suggested.add('altitude-hijack');
    }
    if (rIdx > qIdx + 1) suggested.add('altitude-hijack');
    if (rIdx < qIdx - 1) suggested.add('altitude-hijack');
  }

  if (questionAsked.trim() && responseGiven.trim()) {
    const qLower = questionAsked.toLowerCase();
    if (
      (/\bwho\b|\bserve who\b|\bfor whom\b/.test(qLower) && responseLevel === 'identity') ||
      (/\bhow\b/.test(qLower) && responseLevel === 'values') ||
      (/\bwhat do you do\b/.test(qLower) && responseLevel === 'purpose')
    ) {
      suggested.add('question-substitution');
    }
  }

  if (responseGiven.trim().split(/\s+/).length > 80) {
    suggested.add('coherence-collapse');
  }

  const identityWords = /\b(I am|my calling|my purpose|who I am|identity|philosophy|meaning of life)\b/i;
  const behaviorWords = /\b(tomorrow|specifically|stakeholder|client|team|action|do\b|serve\b)\b/i;
  if (
    questionLevel === 'behavior' &&
    identityWords.test(responseGiven) &&
    !behaviorWords.test(responseGiven)
  ) {
    suggested.add('coherence-collapse');
    suggested.add('altitude-hijack');
  }

  return [...suggested];
}

export function suggestCoachMove(scenario: LevelShiftScenario): string {
  if (!scenario.questionLevel || !scenario.responseLevel) return '';
  const qIdx = levelIndex(scenario.questionLevel);
  const rIdx = levelIndex(scenario.responseLevel);
  const qLabel = LOGICAL_LEVELS.find(l => l.id === scenario.questionLevel)?.label ?? 'question';
  const rLabel = LOGICAL_LEVELS.find(l => l.id === scenario.responseLevel)?.label ?? 'response';

  if (rIdx > qIdx) {
    return `You're answering from ${rLabel}, but the question lives at ${qLabel}. Name the level shift, then return to ${qLabel} before going higher.`;
  }
  if (rIdx < qIdx) {
    return `You're answering from ${rLabel}, but the question requires ${qLabel}. Build up from what you named — don't skip the middle levels.`;
  }
  return `Stay at ${qLabel} until the question is fully answered, then invite the conversation up intentionally.`;
}

export function canAdvance(step: LevelAwareStep, state: LevelAwareState): boolean {
  const s = state.scenario;
  switch (step) {
    case 'intro':
    case 'pattern':
      return true;
    case 'capture':
      return (
        s.questionAsked.trim().length >= 8 &&
        s.responseGiven.trim().length >= 12 &&
        s.questionLevel !== '' &&
        s.responseLevel !== ''
      );
    case 'diagnose':
      return s.markers.length >= 1;
    case 'reanchor':
      return s.reanchoredResponse.trim().length >= 12;
    case 'results':
      return true;
    default:
      return false;
  }
}

export function levelLabel(level: LogicalLevel | ''): string {
  if (!level) return '—';
  return LOGICAL_LEVELS.find(l => l.id === level)?.label ?? level;
}
